import { create } from "zustand";
import { Track } from "@/types/track";
import { supabase } from "./supabase";
import { excludeLabels } from "./excluded-labels"; // [EXCLUDE_LABELS]
import { normalizeTerritories } from "@/components/drafts/licensing/constants";

interface TracksState {
  /** Wipe track list + selection (logout/account switch). */
  reset: () => void;
  tracks: Track[];
  selectedTrackId: string | null; // Changed to string for UUID
  isDetailsOpen: boolean;
  isSelectionMode: boolean;
  selectedTrackIds: Set<string>; // Changed to string for UUID
  fetchTracks: () => Promise<void>; // Add fetch function
  setTracks: (tracks: Track[]) => void;
  selectTrack: (trackId: string) => void; // Changed to string for UUID
  closeDetails: () => void;
  toggleSelectionMode: () => void;
  toggleTrackSelection: (id: string) => void; // Changed to string for UUID
  selectAllTracks: () => void;
  clearSelection: () => void;
  removeTracks: (trackIds: string[]) => void;
  isLoading: boolean;
  error: string | null;
}

/**
 * Map a `tracks` row to the frontend `Track` shape the catalog store and the details panel read.
 *
 * Every column of the row is kept (spread last, so the row's own values win); the explicit
 * fields only fill what a row lacks, and the cover falls back through the older image columns.
 * Shared by `fetchTracks` and `openTrackDetailsById` (lib/open-track-details.ts).
 */
export function toCatalogTrack(dbTrack: Record<string, any>): Track {
  // For cover art, we can keep the original URL as it's usually public
  let signedCoverArtUrl = dbTrack.cover_art_url || dbTrack.artwork_url;

  // Also check for other possible image field names
  if (!signedCoverArtUrl) {
    signedCoverArtUrl =
      dbTrack.cover_image_url || dbTrack.artwork_image_url || dbTrack.image_url;
  }

  return {
    // Required fields
    url: "", // Playback uses backend signed URL endpoint
    id: dbTrack.id,
    created_at: dbTrack.created_at,
    updated_at: dbTrack.updated_at,
    title: dbTrack.title || "Unknown Title",
    user_id: dbTrack.user_id,
    is_published: dbTrack.is_published,
    artist: dbTrack.artist || "",
    progress: 100,
    lastModified: dbTrack.updated_at || dbTrack.created_at,
    duration:
      typeof dbTrack.duration === "string"
        ? dbTrack.duration
        : String(dbTrack.duration || ""),
    metadata: dbTrack.metadata || {},
    tags: Array.isArray(dbTrack.tags) ? dbTrack.tags : [],
    writers: Array.isArray(dbTrack.writers) ? dbTrack.writers : [],
    lyrics: dbTrack.lyrics || "",
    analysis: dbTrack.analysis || null,
    cover_art_url: signedCoverArtUrl,
    artwork_url: signedCoverArtUrl,
    // Include all other fields from the database. They supply the rest of Track (album,
    // bpm, key, ...), which the compiler cannot see through the row's index signature.
    ...dbTrack,
  } as Track;
}

// Request deduplication cache
const requestCache = new Map<string, Promise<any>>();
const CACHE_DURATION = 30000; // 30 seconds
const cacheTimestamps = new Map<string, number>();

// Clear cache function for debugging
export const clearTracksCache = () => {
  requestCache.clear();
  cacheTimestamps.clear();
};

export const useTracksStore = create<TracksState>((set, get) => ({
  tracks: [], // Start with empty array
  selectedTrackId: null,
  isDetailsOpen: false,
  isSelectionMode: false,
  selectedTrackIds: new Set(),
  isLoading: true, // Set loading to true initially
  error: null,

  reset: () =>
    set({
      tracks: [],
      selectedTrackId: null,
      isDetailsOpen: false,
      isSelectionMode: false,
      selectedTrackIds: new Set<string>(),
      isLoading: true,
      error: null,
    }),

  fetchTracks: async () => {
    const cacheKey = "tracks-published";
    const now = Date.now();

    // Check if we have a recent cached request
    if (requestCache.has(cacheKey) && cacheTimestamps.has(cacheKey)) {
      const cacheTime = cacheTimestamps.get(cacheKey)!;
      if (now - cacheTime < CACHE_DURATION) {
        return requestCache.get(cacheKey);
      } else {
        // Remove expired cache
        requestCache.delete(cacheKey);
        cacheTimestamps.delete(cacheKey);
      }
    }

    // Check if already loading to prevent duplicate requests
    // Note: rely on requestCache to dedupe; don't early-return on isLoading,
    // which can get stuck if a prior request errored before toggling the flag.

    set({ isLoading: true, error: null });

    const fetchPromise = (async () => {
      try {
        // [EXCLUDE_LABELS]
        const { data, error } = await excludeLabels(
          supabase
            .from("tracks")
            .select("*")
            .eq("is_published", true)
        ).order("created_at", { ascending: false });

        if (error) throw error;

        const results = data || [];

        const mappedTracks = results.map(toCatalogTrack);

        set({ tracks: mappedTracks, isLoading: false });

        // Cache the successful result
        cacheTimestamps.set(cacheKey, now);

        return data;
      } catch (error: any) {
        console.error("Error fetching tracks:", error);
        set({ error: error.message, isLoading: false });
        throw error;
      } finally {
        // Clean up cache after request completes
        requestCache.delete(cacheKey);
        // Guarantee we don't get stuck in loading state
        set((s) => (s.isLoading ? { isLoading: false } : s));
      }
    })();

    // Cache the promise to prevent duplicate requests
    requestCache.set(cacheKey, fetchPromise);

    return fetchPromise;
  },

  setTracks: (tracks) => set({ tracks }),
  selectTrack: (trackId) =>
    set({ selectedTrackId: trackId, isDetailsOpen: true }),
  closeDetails: () => set({ isDetailsOpen: false }),
  toggleSelectionMode: () =>
    set((state) => ({
      isSelectionMode: !state.isSelectionMode,
      selectedTrackIds: new Set(),
    })),
  toggleTrackSelection: (id) =>
    set((state) => {
      const newSelection = new Set(state.selectedTrackIds);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return { selectedTrackIds: newSelection };
    }),
  selectAllTracks: () =>
    set((state) => {
      // Ensure track.id is correctly accessed
      const allTrackIds = state.tracks.map((track) => String(track.id));
      return { selectedTrackIds: new Set(allTrackIds) };
    }),
  clearSelection: () => set({ selectedTrackIds: new Set() }),

  /**
   * Drop rows the catalogue no longer holds — today, a family moved back to drafts.
   *
   * The write itself is `POST /api/track-families/{id}/move-to-drafts`: the server owns
   * `is_published`, and it flips the main and every live version of it in one statement. This is
   * only the list catching up, plus the sheet, which would otherwise sit open over a track that is
   * no longer in the catalogue it was opened from.
   */
  removeTracks: (trackIds: string[]) => {
    const gone = new Set(trackIds.map(String));
    if (gone.size === 0) return;
    set((state) => {
      const wasOpen =
        state.selectedTrackId !== null && gone.has(String(state.selectedTrackId));
      return {
        tracks: state.tracks.filter((t) => !gone.has(String(t.id))),
        selectedTrackIds: new Set(
          [...state.selectedTrackIds].filter((id) => !gone.has(String(id)))
        ),
        selectedTrackId: wasOpen ? null : state.selectedTrackId,
        isDetailsOpen: wasOpen ? false : state.isDetailsOpen,
      };
    });
    // The page cache would otherwise serve the removed rows back on the next mount.
    clearTracksCache();
  },
}));

export interface LicensingConfiguration {
  licensing_tier: "instant" | "standard" | "bespoke" | null;
  pricing_method: "fixed" | "range";
  fixed_price?: number;
  min_price?: number;
  max_price?: number;
  sweet_spot_price?: number;
  allowed_usage_types: string[];
  licensing_restrictions?: string;
  territories_included?: string[];
}

export class TrackLicensingService {
  /**
   * Save licensing configuration for a track
   */
  static async saveLicensingConfiguration(
    trackId: string,
    config: LicensingConfiguration
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Prepare the update data
      const updateData: any = {
        licensing_tier: config.licensing_tier,
        pricing_method: config.pricing_method,
        allowed_usage_types: config.allowed_usage_types,
        licensing_restrictions: config.licensing_restrictions || null,
        territories_included: config.territories_included || [],
        updated_at: new Date().toISOString(),
      };

      // Add pricing fields based on method
      if (config.pricing_method === "fixed") {
        updateData.fixed_price = config.fixed_price;
        updateData.min_price = null;
        updateData.max_price = null;
        updateData.sweet_spot_price = null;
      } else if (config.pricing_method === "range") {
        updateData.fixed_price = null;
        updateData.min_price = config.min_price;
        updateData.max_price = config.max_price;
        updateData.sweet_spot_price = config.sweet_spot_price;
      }

      // Update the track in Supabase
      const { error } = await supabase
        .from("tracks")
        .update(updateData)
        .eq("id", trackId)
        .select();

      if (error) {
        console.error("❌ Error saving licensing configuration:", error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      console.error("❌ Error in saveLicensingConfiguration:", error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get licensing configuration for a track
   */
  static async getLicensingConfiguration(
    trackId: string
  ): Promise<LicensingConfiguration | null> {
    try {
      const { data, error } = await supabase
        .from("tracks")
        .select(
          `
          licensing_tier,
          pricing_method,
          fixed_price,
          min_price,
          max_price,
          sweet_spot_price,
          allowed_usage_types,
          licensing_restrictions,
          territories_included
        `
        )
        .eq("id", trackId)
        .single();

      if (error) {
        console.error("❌ Error fetching licensing configuration:", error);
        return null;
      }

      if (!data) {
        return null;
      }

      // Parse the JSON usage types
      const usageTypes =
        typeof data.allowed_usage_types === "string"
          ? JSON.parse(data.allowed_usage_types)
          : data.allowed_usage_types || [];

      // DB columns are plain text / nullable; coerce back to the domain unions the
      // licensing UI relies on (values are constrained by the app, not the schema).
      return {
        licensing_tier:
          data.licensing_tier as LicensingConfiguration["licensing_tier"],
        pricing_method: (data.pricing_method ?? "fixed") as "fixed" | "range",
        fixed_price: data.fixed_price ?? undefined,
        min_price: data.min_price ?? undefined,
        max_price: data.max_price ?? undefined,
        sweet_spot_price: data.sweet_spot_price ?? undefined,
        allowed_usage_types: usageTypes,
        licensing_restrictions: data.licensing_restrictions ?? undefined,
        territories_included: normalizeTerritories(data.territories_included),
      };
    } catch (error: any) {
      console.error("❌ Error in getLicensingConfiguration:", error);
      return null;
    }
  }

  /**
   * Search tracks by licensing criteria
   */
  static async searchTracksByLicensing(criteria: {
    licensing_tier?: string[];
    max_budget?: number;
    usage_types?: string[];
  }) {
    try {
      let query = supabase
        .from("tracks")
        .select(
          `
          id, title, artist, licensing_tier, pricing_method,
          fixed_price, min_price, max_price, sweet_spot_price,
          allowed_usage_types, licensing_restrictions
        `
        )
        .eq("is_published", true);

      // Filter by licensing tier
      if (criteria.licensing_tier && criteria.licensing_tier.length > 0) {
        query = query.in("licensing_tier", criteria.licensing_tier);
      }

      // Filter by budget
      if (criteria.max_budget) {
        query = query.or(
          `fixed_price.lte.${criteria.max_budget},min_price.lte.${criteria.max_budget}`
        );
      }

      const { data, error } = await query;

      if (error) {
        console.error("❌ Error searching tracks by licensing:", error);
        return [];
      }

      return data || [];
    } catch (error: any) {
      console.error("❌ Error in searchTracksByLicensing:", error);
      return [];
    }
  }
}
